import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ImportScheduleModal = ({ isOpen, onClose, onImport, teams }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importMode, setImportMode] = useState('replace'); // 'replace' or 'add'

  // Proper CSV parser that handles quoted values with commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line?.length; i++) {
      const char = line?.[i];
      const nextChar = line?.[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator outside quotes
        result?.push(current?.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add the last field
    result?.push(current?.trim());
    return result;
  };

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile?.name?.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        
        if (lines?.length < 2) {
          setError('CSV file is empty or invalid');
          return;
        }

        const headers = parseCSVLine(lines?.[0])?.map(h => h?.trim()?.toLowerCase());
        const requiredHeaders = ['match_number', 'team1', 'team2', 'date', 'time', 'venue', 'match_type'];
        
        const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));
        if (missingHeaders?.length > 0) {
          setError(`Missing required columns: ${missingHeaders?.join(', ')}`);
          return;
        }

        const data = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = parseCSVLine(lines?.[i]);
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });
          
          if (row?.match_number && row?.team1 && row?.team2) {
            data?.push(row);
          }
        }

        if (data?.length === 0) {
          setError('No valid match data found in CSV');
          return;
        }

        setPreview(data?.slice(0, 5));
        setError('');
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parsing error:', err);
      }
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!file || preview?.length === 0) return;

    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        const headers = parseCSVLine(lines?.[0])?.map(h => h?.trim()?.toLowerCase());
        
        const matches = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = parseCSVLine(lines?.[i]);
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (!row?.match_number || !row?.team1 || !row?.team2) continue;

          const team1 = teams?.find(t => 
            t?.name?.toLowerCase() === row?.team1?.toLowerCase()
          );
          const team2 = teams?.find(t => 
            t?.name?.toLowerCase() === row?.team2?.toLowerCase()
          );

          if (!team1 || !team2) {
            console.warn(`Skipping match ${row?.match_number}: Team not found`);
            continue;
          }

          const matchTypeMap = {
            'group': 'group',
            'group stage': 'group',
            'quarter-final': 'quarter-final',
            'quarter final': 'quarter-final',
            'semi-final': 'semi-final',
            'semi final': 'semi-final',
            'final': 'final'
          };

          const matchType = matchTypeMap?.[row?.match_type?.toLowerCase()] || 'group';
          const stage = matchType?.replace('-', '_');

          matches?.push({
            matchNumber: parseInt(row?.match_number),
            team1Id: team1?.id,
            team2Id: team2?.id,
            date: row?.date,
            time: row?.time || '14:30',
            venue: row?.venue || 'TBD',
            matchType: matchType,
            stage: stage
          });
        }

        if (matches?.length === 0) {
          setError('No valid matches found. Please check team names match exactly.');
          setIsProcessing(false);
          return;
        }

        await onImport(matches, importMode);
        handleClose();
      } catch (err) {
        setError('Failed to import matches. Please try again.');
        console.error('Import error:', err);
      } finally {
        setIsProcessing(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setPreview([]);
    setIsProcessing(false);
    setImportMode('replace');
    onClose();
  };

  const downloadTemplate = () => {
    const headers = 'match_number,team1,team2,date,time,venue,match_type';
    const example = '1,India,Australia,2026-03-15,14:30,"Sinhalese Sports Club, Colombo",group';
    const csv = `${headers}\n${example}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schedule_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Import Schedule from CSV</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">CSV Format Requirements</h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Required columns: match_number, team1, team2, date, time, venue, match_type</li>
                  <li>• Date format: YYYY-MM-DD (e.g., 2026-03-15)</li>
                  <li>• Time format: HH:MM (e.g., 14:30)</li>
                  <li>• Match types: group, quarter-final, semi-final, final</li>
                  <li>• Team names must match exactly with tournament teams</li>
                  <li>• Use double quotes around values with commas (e.g., "Sinhalese Sports Club, Colombo")</li>
                </ul>
                <Button
                  variant="link"
                  onClick={downloadTemplate}
                  iconName="Download"
                  iconPosition="left"
                  className="mt-2 text-blue-600 dark:text-blue-400 p-0 h-auto"
                >
                  Download Template CSV
                </Button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Import Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="importMode"
                  value="replace"
                  checked={importMode === 'replace'}
                  onChange={(e) => setImportMode(e?.target?.value)}
                  className="w-4 h-4 text-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-foreground">Replace Existing Schedule</div>
                  <div className="text-sm text-muted-foreground">Delete all existing matches and import new schedule</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="importMode"
                  value="add"
                  checked={importMode === 'add'}
                  onChange={(e) => setImportMode(e?.target?.value)}
                  className="w-4 h-4 text-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-foreground">Add to Existing Schedule</div>
                  <div className="text-sm text-muted-foreground">Keep existing matches and add new ones from CSV</div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select CSV File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Icon name="Upload" size={48} className="text-muted-foreground" />
                <div>
                  <p className="text-foreground font-medium">Click to upload CSV file</p>
                  <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                </div>
              </label>
            </div>
            {file && (
              <div className="mt-3 flex items-center gap-2 text-sm text-foreground">
                <Icon name="FileText" size={16} />
                <span>{file?.name}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {preview?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Preview (First 5 matches)</h4>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Match #</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Team 1</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Team 2</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Date</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Time</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Venue</th>
                        <th className="px-4 py-2 text-left font-medium text-foreground">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview?.map((row, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="px-4 py-2 text-muted-foreground">{row?.match_number}</td>
                          <td className="px-4 py-2 text-foreground">{row?.team1}</td>
                          <td className="px-4 py-2 text-foreground">{row?.team2}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.date}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.time}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.venue}</td>
                          <td className="px-4 py-2 text-muted-foreground">{row?.match_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleImport}
            disabled={!file || preview?.length === 0 || isProcessing}
            iconName={isProcessing ? 'Loader' : 'Upload'}
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            {isProcessing ? 'Importing...' : 'Import Schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImportScheduleModal;